import { useCallback, useEffect, useMemo, useState } from "react";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { publicAccountsService } from "../services/publicAccounts.service";
import {
  TRACKING_PAGE_SIZE,
  TRACKING_PAGINATION_SIZES,
} from "../constants/public-accounts.constants";
import type { PublicAccountItem } from "../types/public-accounts-api.types";

function getPageNumbers(
  currentPage: number,
  lastPage: number,
  _canGoNext: boolean
): (number | string)[] {
  const maxPagesToShow = 7;
  if (lastPage <= maxPagesToShow) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", lastPage];
  }
  if (currentPage >= lastPage - 2) {
    return [1, "...", lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", lastPage];
}

export function useAccountsWalletsTrackingPage(searchParam: string) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TRACKING_PAGE_SIZE);
  const [rows, setRows] = useState<PublicAccountItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoAccess, setHasNoAccess] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setHasNoAccess(false);
    try {
      const term = searchParam.trim() || undefined;
      const skip = (page - 1) * pageSize;
      const response = await publicAccountsService.fetchList({
        skip,
        limit: pageSize,
        search: term,
      });
      setRows(response.data);
      setTotalItems(response.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setHasNoAccess(true);
        setRows([]);
        setTotalItems(0);
        return;
      }
      const message = HttpApiService.getErrorMessage(err);
      setError(message ?? "Error al cargar las cuentas");
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchParam, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchParam]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrevious = page > 1;
  const canGoNext = page < lastPage;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const paginationOptions = useMemo(
    () =>
      TRACKING_PAGINATION_SIZES.map((size) => ({
        label: String(size),
        value: String(size),
      })),
    []
  );

  const pageNumbers = useMemo(
    () => getPageNumbers(page, lastPage, canGoNext),
    [page, lastPage, canGoNext]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  return {
    rows,
    totalItems,
    page,
    pageSize,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    hasNoAccess,
    paginationOptions,
    pageNumbers,
    handlePageChange,
    handleItemsPerPageChange,
    refetch: fetchAccounts,
  };
}
